import type { JSONContent } from '@tiptap/core'

const EMBED_NODE_TYPES = new Set(['image', 'mention', 'mergeTag', 'ctaButton'])

const textOf = (node: JSONContent): string => {
  if (node.type === 'text') return node.text ?? ''
  return (node.content ?? []).map(textOf).join('')
}

const hasEmbed = (node: JSONContent): boolean => {
  if (node.type && EMBED_NODE_TYPES.has(node.type)) return true
  return (node.content ?? []).some(hasEmbed)
}

const isBlankBlock = (node: JSONContent): boolean =>
  textOf(node).trim() === '' && !hasEmbed(node)

const trimBlockStart = (block: JSONContent): JSONContent => {
  if (!block.content || block.content.length === 0) return block
  const content = [...block.content]
  while (content.length > 0) {
    const first = content[0]!
    if (first.type === 'hardBreak') {
      content.shift()
      continue
    }
    if (first.type !== 'text') break
    const trimmed = (first.text ?? '').replace(/^\s+/, '')
    if (trimmed === '') {
      content.shift()
      continue
    }
    content[0] = { ...first, text: trimmed }
    break
  }
  return { ...block, content }
}

const trimBlockEnd = (block: JSONContent): JSONContent => {
  if (!block.content || block.content.length === 0) return block
  const content = [...block.content]
  while (content.length > 0) {
    const last = content[content.length - 1]!
    if (last.type === 'hardBreak') {
      content.pop()
      continue
    }
    if (last.type !== 'text') break
    const trimmed = (last.text ?? '').replace(/\s+$/, '')
    if (trimmed === '') {
      content.pop()
      continue
    }
    content[content.length - 1] = { ...last, text: trimmed }
    break
  }
  return { ...block, content }
}

/**
 * Strips leading/trailing whitespace and empty blocks (paragraphs, empty
 * headings, etc.) from a TipTap doc. Whitespace and empty blocks in the middle
 * are preserved. Embed nodes (image, mention, mergeTag, ctaButton) counts as a
 * block even when there's no text.
 */
export function trimContent(doc: JSONContent): JSONContent {
  if (doc.type !== 'doc' || !doc.content) return doc
  const blocks = [...doc.content]
  while (blocks.length > 0 && isBlankBlock(blocks[0]!)) blocks.shift()
  while (blocks.length > 0 && isBlankBlock(blocks[blocks.length - 1]!)) {
    blocks.pop()
  }
  if (blocks.length > 0) {
    blocks[0] = trimBlockStart(blocks[0]!)
    blocks[blocks.length - 1] = trimBlockEnd(blocks[blocks.length - 1]!)
  }
  return { ...doc, content: blocks }
}
